import { connect } from 'react-redux';
import { useState, useRef, useEffect } from 'react';

import { 
  SiteboardApiValidator, ADD_PROJECT_MEMBER, SET_REVIEW_SHARED_SCHEDULE_VIEWS_DIALOGUE_OPEN, PUBLISH_SCHEDULE, SET_EDIT_PROJECT_DIALOGUE_ID,
} from '../actions/types';
import { setPath, siteboardAction, setNewScheduleDialogueId, setPublishScheduleDialogueId, } from '../actions/defaultActions';
import { keyCountInObject } from '../Util';

import PushButton from './PushButton';

// icon imports
import { PlusIcon, UsersIcon, ContactRound, Send, Eye, } from 'lucide-react';
import LightningIcon from '../icons/lightningIcon';
import ChecklistIcon from '../icons/checklistIcon';
import SendIcon from '../icons/sendIcon';
import ContactIcon from '../icons/contactIcon';
import TeamIcon from '../icons/teamIcon';

const PerformanceMeter = ({ _projects, _schedules, _users, _userId, _subscriptions, _activities, setNewScheduleDialogueId, setPath, siteboardAction, selectedProjectId, selectedScheduleId, setPublishScheduleDialogueId }) => {

  const [meterPinging, setMeterPinging] = useState(true);
  const [meterInfoShowing, setMeterInfoShowing] = useState(false);

  const selectedProject = selectedProjectId in _projects ? _projects[selectedProjectId] : null;
  const selectedSchedule = selectedScheduleId in _schedules ? _schedules[selectedScheduleId] : null;
  const selectedSubscription = selectedProject !== null ? _subscriptions[selectedProject.subscriptionId] : null;

  const scheduleIdRef = useRef(selectedScheduleId); // used to re-enable pulsing ring when the project changes

  useEffect(() => {
    if(!scheduleIdRef.current) return;
    if(scheduleIdRef.current === selectedScheduleId) return;

    scheduleIdRef.current = selectedScheduleId;
    setMeterPinging(true);
  }, [selectedScheduleId]);


  if(!selectedProject || !selectedSubscription) return (<></>);

  const userCanPublishSchedule = () => {
    if (selectedSchedule === null) return false;
    if (selectedSchedule.isPublished === true) return false;
    return SiteboardApiValidator[PUBLISH_SCHEDULE].isUserIdAuthorized(_userId, selectedSubscription, selectedProject);
  };

  const memberCountInActiveProject = keyCountInObject(selectedProject.members) + keyCountInObject(selectedProject.pendingMembers);

  const scheduleCreated = keyCountInObject(selectedProject.schedules) > 0;
  const activitiesAdded = (() => {
    if(!selectedSchedule) return false;
    for(const activityId in _activities) {
      const activity = _activities[activityId];
      if(activity.scheduleId !== selectedSchedule.id) continue;
      if(activity.responsible.length === 0) continue;
      if(activity.description.length === 0) continue;
      if(keyCountInObject(activity.activeDates) === 0) continue;
      if(activity.responsible.trim().length === 0) continue;
      if(activity.description.trim().length === 0) continue;
      // TODO: maybe check if activity has an active date within the schedule date range
      return true;
    }
    return false;
  })();
  const teamInvited = memberCountInActiveProject > 1;
  const distributionContactsAdded = selectedProject.contactCount > 0;
  const schedulePublished = selectedSchedule && selectedSchedule.isPublished === true;
  const powerLevel = 0 + (scheduleCreated ? 1 : 0) + (activitiesAdded ? 1 : 0) + (teamInvited ? 1 : 0) + (distributionContactsAdded ? 1 : 0) + (schedulePublished ? 1 : 0);

  const powerCircleSize = 75;
  const percent = powerLevel*20;
  const strokeWidth = 12;
  const radius = (powerCircleSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(100, percent));
  const dash = (progress / 100) * circumference;

  return (
    <div className='p-2 py-4 bg-neutral-900 border border-neutral-700 rounded-lg'>
      <div className='flex flex-col gap-2'>

        {/* Header */}
        <div 
          className='flex flex-col justify-between gap-2'
          onMouseEnter={() => { 
            setMeterPinging(false); 
            setMeterInfoShowing(true);
          }}
          onMouseLeave={() => {
            setMeterInfoShowing(false);
          }} 
        >
          {/* Header - Text */}
          <div className='flex items-start gap-2 text-[1em] text-[#e7e8e8]'>
            <LightningIcon color='#f9a928' size='1.2em' />
            <div className='uppercase font-bold'>Schedule Power</div>
          </div>

          {/* Header - Circle */}
          <div className='relative flex justify-center my-1'>
            <div 
              className='relative z-[1] rounded-full bg-[#f9a928]' 
              style={{
                width: `${powerCircleSize-1}px`,
                height: `${powerCircleSize-1}px`,
              }}
            />

            {/* Progress arc */}
            {powerLevel > 0 &&
              <svg
                width={powerCircleSize}
                height={powerCircleSize}
                viewBox={`0 0 ${powerCircleSize} ${powerCircleSize}`}
                className="-rotate-90 absolute z-[2] left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%]"
              >
                <circle
                  cx={powerCircleSize / 2}
                  cy={powerCircleSize / 2}
                  r={radius}
                  fill="none"
                  stroke='#349951'
                  strokeWidth={strokeWidth}
                  strokeLinecap="butt" // round
                  strokeDasharray={`${dash} ${circumference}`}
                />
              </svg>
            }
            <div 
              className='absolute z-[3] left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%] rounded-full bg-neutral-900' 
              style={{
                width: `${powerCircleSize-20}px`,
                height: `${powerCircleSize-20}px`,
              }}
            />
            <div className='absolute z-[4] left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%] font-bold text-[1.5em] tracking-widest'>{powerLevel}/5</div>

            {/*<div className="absolute z-[5] h-[112px] w-[112px] rounded-full animate-spin [animation-duration:6s]">
              <div
                className="
                  h-full w-full rounded-full
                  bg-[conic-gradient(from_0deg,#f9a928,transparent,#34d399,transparent,#f9a928)]
                  blur-lg opacity-80
                "
              />
            </div>*/}

            {/* Animated glow */}
            <div
              className={`absolute rounded-full bg-amber-400/40 blur-xl animate-power-glow opacity-80`}
              style={{
                width: `${powerCircleSize+20}px`,
                height: `${powerCircleSize+20}px`,
              }}
            />

            {/* Pulsing ring */}
            <div
              className={`
                absolute rounded-full
                border-2 border-amber-300/60
                ${(meterPinging && powerLevel < 5) ? 'opacity-40 animate-ping' : 'opacity-0'}
              `}
              style={{
                width: `${powerCircleSize+10}px`,
                height: `${powerCircleSize+10}px`,
              }}
            />

            {/* Overlay */}
            <div
              className={`
                absolute top-[-10px] z-[6] px-2 py-2
                flex items-center justify-center
                rounded-xl
                bg-neutral-950/90 backdrop-blur-sm
                transition-all duration-300
                ${meterInfoShowing ? 'opacity-100' : 'opacity-0'}
                select-none
                cursor-pointer
              `}
              onClick={() => { setMeterInfoShowing(false); }}
            >
              <div className="max-w-[220px] text-center">
                <h3 className="font-semibold text-white leading-[1em]">
                  {powerLevel < 5 
                    ? 'Complete Your Setup' 
                    : 'Full Potential Unlocked'
                  }
                </h3>
                <p className="mt-2 text-sm text-neutral-300 leading-[1.2em]">
                  {powerLevel < 5
                    ? `Get the most out of ${process.env.REACT_APP_WEBSITE_NAME} by fully setting up your project and publishing a schedule.`
                    : `Congratulations! Your project is fully configured and you're taking full advantage of ${process.env.REACT_APP_WEBSITE_NAME}.`
                  }
                  
                </p>
              </div>
            </div>

          </div>
        </div>

        <PowerItem 
          notCompletedText='Create Schedule'
          notCompletedButton={
            <PushButton 
              isInline={true}
              textColor={'white'}
              bgColor={'bg-neutral-600'}
              hoverBgColor={'bg-neutral-500'}
              onClick={() => { setNewScheduleDialogueId(selectedProject.id, null); }}
            >
              Create
            </PushButton>
          }
          completedText='Schedule Created' 
          completed={scheduleCreated} 
          glowing={meterInfoShowing}
        />
        <div className='mx-1 h-[1px] bg-neutral-700'></div>
        <PowerItem 
          notCompletedText='Add Scheduled Activities'
          notCompletedDescription='Break your work into activities'
          completedText='Schedule Activities Added'
          completedDescription='' 
          completed={activitiesAdded} 
          glowing={meterInfoShowing}
        />
        <div className='mx-1 h-[1px] bg-neutral-700'></div>
        <PowerItem 
          notCompletedText='Invite Your Team'
          notCompletedDescription='Team members can create / edit & publish schedules'
          notCompletedButton={
            (memberCountInActiveProject <= 1 && SiteboardApiValidator[ADD_PROJECT_MEMBER].isUserIdAuthorized(_userId, selectedSubscription, selectedProject)) ?
              <PushButton 
                isInline={true}
                textColor={'white'}
                bgColor={'bg-neutral-600'}
                hoverBgColor={'bg-neutral-500'}
                onClick={() => { siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: selectedProject.id, tabName: 'Team' }); }}
              >
                <div className='flex items-center gap-1'>
                  <PlusIcon size='1.2em' color='#fff' /> Invite 
                </div>
              </PushButton>
              : 
              <></>
          }
          completedText={`Project Members (${keyCountInObject(selectedProject.members)})`}
          completedDescription='' 
          completedButton={
            <PushButton 
              isInline={true}
              textColor={'white'}
              bgColor={'bg-neutral-600'}
              hoverBgColor={'bg-neutral-500'}
              onClick={() => { siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: selectedProject.id, tabName: 'Team' }); }}
            >
              <div className='flex items-center gap-1'>
                <UsersIcon size='1.2em' color='#fff' /> Team
              </div>
            </PushButton>
          }
          completed={teamInvited} 
          glowing={meterInfoShowing}
        />
        <div className='mx-1 h-[1px] bg-neutral-700'></div>
        <PowerItem 
          notCompletedText='Add Distribution List Contacts'
          notCompletedDescription='Let contacts receive published schedules'
          notCompletedButton={
            <PushButton 
              isInline={true}
              textColor={'white'}
              bgColor={'bg-neutral-600'}
              hoverBgColor={'bg-neutral-500'}
              onClick={() => {
                siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: selectedProject.id, tabName: 'Distribution' });
              }}
            >
              <div className='flex items-center gap-1'>
                <PlusIcon size='1.2em' color='#fff' /> Add 
              </div>
            </PushButton>
          }
          completedText={`Distribution List (${selectedProject.contactCount})`}
          completedDescription='' 
          completedButton={
            <PushButton 
              isInline={true}
              textColor={'white'}
              bgColor={'bg-neutral-600'}
              hoverBgColor={'bg-neutral-500'}
              onClick={() => {
                siteboardAction(SET_EDIT_PROJECT_DIALOGUE_ID, { projectId: selectedProject.id, tabName: 'Distribution' });
              }}
            >
              <div className='flex items-center gap-1'>
                <ContactRound size='1.2em' color='#fff' /> List
              </div>
            </PushButton>
          }
          completed={distributionContactsAdded} 
          glowing={meterInfoShowing}
        />
        <div className='mx-1 h-[1px] bg-neutral-700'></div>
        <PowerItem 
          notCompletedText='Publish Schedule'
          notCompletedDescription='Send schedule to distribution list'
          notCompletedButton={
            <PushButton 
              isInline={true}
              textColor={'white'}
              bgColor={'bg-neutral-600'}
              hoverBgColor={'bg-neutral-500'}
              disabled={!userCanPublishSchedule()}
              onClick={() => {
                if(!selectedSchedule) return;
                setPublishScheduleDialogueId(selectedSchedule.id);
              }}
            >
              <div className='flex items-center gap-1'>
                <Send size='1.2em' color='#fff' /> Publish
              </div>
            </PushButton>
          }
          completedText='Schedule Published'
          completedDescription='Distribution list has been emailed the schedule.' 
          completedButton={
            <PushButton 
              isInline={true}
              textColor={'white'}
              bgColor={'bg-neutral-600'}
              hoverBgColor={'bg-neutral-500'}
              onClick={() => {
                if(!selectedSchedule) return;
                //siteboardAction(SET_REVIEW_SHARED_SCHEDULE_VIEWS_DIALOGUE_OPEN, { isOpen: true, scheduleId: selectedSchedule.id });
                setPublishScheduleDialogueId(selectedSchedule.id);
              }}
            >
              
              <div className='flex items-center gap-1'>
                <Eye size='1.2em' color='#fff' /> Views 
              </div>
            </PushButton>
          }
          completed={schedulePublished} 
          glowing={meterInfoShowing}
        />
      </div>
    </div>
  );
};

const PowerItem = ({notCompletedText, notCompletedDescription, notCompletedButton, completedText, completedDescription, completedButton, completed=false, glowing=false}) => {
  return (
    <div className='flex items-center gap-2 text-sm'>
      <div>
        {completed === true && <ChecklistIcon size='1.5em' color='#349951' />}
        {completed !== true && 
          <div className={`relative h-[1.6em] w-[1.6em] rounded-full`}>
            <div className={`absolute top-0 left-0 h-[1.5em] w-[1.5em] rounded-full border-[1.6px] border-[#f9a928] ${glowing ? 'animate-gas-ball bg-[#f9a92833]' : ''}`}></div>
          </div>
        }
      </div>
      <div className='flex flex-col gap-1.5'>
        <div className='font-medium leading-[1.1em]'>{completed === true ? completedText : notCompletedText}</div>
        <div className='text-[#aaa] leading-[1.1em]'>{completed === true ? completedDescription : notCompletedDescription}</div>
      </div>
      <div className={`flex justify-end grow`}>
        {completed === true ? completedButton : notCompletedButton}
      </div>
    </div>
  );
};

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _projects: state.serverSynced.projects,
  _schedules: state.serverSynced.schedules,
  _activities: state.serverSynced.activities,
  _users: state.serverSynced.users,
  _userId: state.localPersistent.userId,
  _subscriptions: state.serverSynced.subscriptions,
}};
export default connect(mapStateToProps, {siteboardAction, setNewScheduleDialogueId, setPublishScheduleDialogueId, })(PerformanceMeter);
